import { apiUrl } from './apiUrl';

/**
 * Token 管理器 — 處理 access token 和 refresh token 的存取與自動續期
 */
const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";
const EXPIRES_AT_KEY = "tokenExpiresAt";
const MUST_CHANGE_PASSWORD_KEY = "mustChangePassword";
const BRAND_CATALOG_KEY = "brandCatalog";
export const BRAND_CATALOG_CHANGE_EVENT = 'brand-catalog-change';

export type Brand = string;
export type BrandScope = Brand[] | "all";
export const DEFAULT_BRAND: Brand = "kailian";
const BRAND_CODE_PATTERN = /^[a-z][a-z0-9_]{1,15}$/;

export interface BrandCatalogOption {
  value: Brand;
  label: string;
  boStatus?: "active" | "reserved" | "disabled";
}

export const BRAND_OPTIONS: BrandCatalogOption[] = [
  { value: "kailian", label: "開連", boStatus: "reserved" },
  { value: "kshield", label: "K盾", boStatus: "active" },
  { value: "wudi_star", label: "無敵星星", boStatus: "active" },
];

export function getBrandOptions(): BrandCatalogOption[] {
  try {
    const raw = localStorage.getItem(BRAND_CATALOG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.filter(
        (item): item is BrandCatalogOption =>
          typeof item?.value === "string" &&
          BRAND_CODE_PATTERN.test(item.value) &&
          typeof item?.label === "string",
      );
    }
  } catch {
    // fall through to seed catalog
  }
  return BRAND_OPTIONS;
}

export function getBrandLabel(
  brand: Brand | string | null | undefined,
): string {
  return (
    getBrandOptions().find((option) => option.value === brand)?.label ??
    brand ??
    "開連"
  );
}

/** 正在進行的 refresh 請求（防止並發多次 refresh） */
let refreshPromise: Promise<string | null> | null = null;

/**
 * 解析 JWT payload
 */
function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

function isBrand(value: unknown): value is Brand {
  return typeof value === "string" && BRAND_CODE_PATTERN.test(value);
}

function normalizeBrand(value: unknown): Brand {
  return isBrand(value) ? value : DEFAULT_BRAND;
}

function normalizeBrandScope(value: unknown): BrandScope {
  if (value === "all") return "all";
  if (!Array.isArray(value)) return [];

  const brands = value.filter(isBrand);
  return brands.length > 0 ? Array.from(new Set(brands)) : [];
}

/**
 * 檢查 access token 是否即將過期（提前 60 秒視為過期）
 */
function isTokenExpired(): boolean {
  const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  if (!expiresAt) {
    // 嘗試從 token 本身解析
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return true;
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;
    return (payload.exp as number) < Math.floor(Date.now() / 1000) + 60;
  }
  // expiresAt 存的是毫秒時間戳
  return Date.now() > Number(expiresAt) - 60_000;
}

export const tokenManager = {
  /**
   * 儲存登入後取得的 token 組合
   */
  setTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    mustChangePassword?: boolean,
  ) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    // expiresIn 是秒數，轉成毫秒時間戳
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));

    if (typeof mustChangePassword === "boolean") {
      this.setMustChangePassword(mustChangePassword);
    }
  },

  /**
   * 取得有效的 access token，如過期會自動 refresh
   * 回傳 null 表示無法取得有效 token（需重新登入）
   */
  async getToken(): Promise<string | null> {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return null;

    // token 尚未過期，直接回傳
    if (!isTokenExpired()) return token;

    // 需要 refresh — 使用單一 promise 防止並發
    if (!refreshPromise) {
      refreshPromise = this.refresh().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  },

  /**
   * 使用 refresh token 換取新的 token pair
   */
  async refresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      this.clear();
      return null;
    }

    try {
      const response = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const result = await response.json();

      if (result.code === 0 && result.data) {
        const { token, refreshToken: newRefreshToken, expiresIn } = result.data;
        this.setTokens(token, newRefreshToken, expiresIn);
        return token;
      }

      // refresh 失敗，清除所有 token
      this.clear();
      return null;
    } catch {
      this.clear();
      return null;
    }
  },

  /**
   * 取得目前存放的 access token（不觸發 refresh）
   */
  getRawToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getPayload(): Record<string, unknown> | null {
    const token = this.getRawToken();
    return token ? parseJwt(token) : null;
  },

  getActiveBrand(): Brand {
    const payload = this.getPayload();
    return normalizeBrand(payload?.activeBrand ?? payload?.brand);
  },

  getBrandScope(): BrandScope {
    const payload = this.getPayload();
    return normalizeBrandScope(payload?.brandScope);
  },

  getAllowedBrands(): Brand[] {
    const scope = this.getBrandScope();
    return scope === "all"
      ? getBrandOptions().map((option) => option.value)
      : scope;
  },

  setBrandCatalog(
    brands: Array<{
      code?: string;
      value?: string;
      label?: string;
      boStatus?: string;
    }>,
  ) {
    const options: BrandCatalogOption[] = brands.flatMap((item) => {
      const status =
        item.boStatus === "active" ||
        item.boStatus === "reserved" ||
        item.boStatus === "disabled"
          ? item.boStatus
          : undefined;
      const value = item.code ?? item.value ?? "";
      const label = item.label ?? item.code ?? item.value ?? "";
      if (!BRAND_CODE_PATTERN.test(value) || label.length === 0) {
        return [];
      }
      return [{ value, label, boStatus: status }];
    });
    if (options.length > 0) {
      localStorage.setItem(BRAND_CATALOG_KEY, JSON.stringify(options));
      window.dispatchEvent(new Event(BRAND_CATALOG_CHANGE_EVENT));
    }
  },

  getActiveBrandLabel(): string {
    return getBrandLabel(this.getActiveBrand());
  },

  isAllBrandAdmin(): boolean {
    return this.getBrandScope() === "all";
  },

  /**
   * 取得 refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * 管理員是否必須先修改密碼
   */
  mustChangePasswordRequired(): boolean {
    return localStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === "1";
  },

  /**
   * 設定是否要求管理員修改密碼
   */
  setMustChangePassword(required: boolean) {
    if (required) {
      localStorage.setItem(MUST_CHANGE_PASSWORD_KEY, "1");
      return;
    }
    localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
  },

  /**
   * 清除所有 token（登出用）
   */
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
    localStorage.removeItem(BRAND_CATALOG_KEY);
  },

  /**
   * 是否持有 token（不檢查是否過期）
   */
  hasToken(): boolean {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  },
};
